import { IAuditLogChange } from '@/Interfaces/AuditLogChange.interface';
import CloseButton from '@/components/Modal/common/CloseButton';
import ModalBody from '@/components/Modal/common/ModalBody';
import { useAuditLogChanges } from '@/hooks/useAuditLogChanges';
import {
  CircularProgress,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import moment from 'moment';
import { Fragment, useMemo } from 'react';

interface IProps {
  open: boolean;
  onClose: () => void;
  auditLogId: number | null;
  summary?: string | null;
}

const ACTION_LABEL: Record<IAuditLogChange['action'], string> = {
  create: 'Added',
  update: 'Updated',
  delete: 'Removed',
};

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/;

const formatDisplay = (value: string | null): string => {
  if (value == null) return '—';
  if (ISO_DATE_REGEX.test(value)) {
    const m = moment(value);
    if (m.isValid()) return m.format('MMM D, YYYY h:mm A');
  }
  return value;
};

const humanizeEntityType = (type: string): string => type.replace(/([a-z])([A-Z])/g, '$1 $2');

const AuditLogChangesModal = ({ open, onClose, auditLogId, summary }: IProps) => {
  const { changes, loading, error } = useAuditLogChanges(open ? auditLogId : null);

  const grouped = useMemo(() => {
    const byKey = new Map<string, IAuditLogChange[]>();
    for (const c of changes) {
      const key = `${c.entityType}#${c.entityId}`;
      const existing = byKey.get(key) ?? [];
      existing.push(c);
      byKey.set(key, existing);
    }
    return Array.from(byKey.entries());
  }, [changes]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalBody className='!w-[1200px] !max-w-[95vw]'>
        <p className='pl-4 pt-2 mb-1 text-xl font-semibold'>Specific Changes</p>
        {summary && <p className='pl-4 mb-6 text-sm text-[#84818A]'>{summary}</p>}
        <div className='absolute right-[24px] top-[24px] flex items-center gap-2'>
          <CloseButton onClick={onClose} />
        </div>

        <div className='mx-4'>
          {loading && (
            <div className='flex items-center justify-center py-10'>
              <CircularProgress size={28} />
            </div>
          )}

          {!loading && error && (
            <div className='py-6 text-center text-red-600'>Failed to load changes: {error.message}</div>
          )}

          {!loading && !error && changes.length === 0 && (
            <div className='py-6 text-center text-[#84818A]'>No field-level changes recorded for this event.</div>
          )}

          {!loading && !error && changes.length > 0 && (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '22%' }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '32%' }}>From</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '32%' }}>To</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '14%' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grouped.map(([key, rows]) => (
                    <Fragment key={key}>
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: '#F4F6F5',
                            color: '#1B4332',
                            borderTop: '1px solid #E5E7EB',
                          }}
                        >
                          {humanizeEntityType(rows[0].entityType)} #{rows[0].entityId}
                        </TableCell>
                      </TableRow>
                      {rows.map(c => (
                        <TableRow key={c.id}>
                          <TableCell>{c.fieldLabel}</TableCell>
                          <TableCell>{formatDisplay(c.oldDisplay)}</TableCell>
                          <TableCell>{formatDisplay(c.newDisplay)}</TableCell>
                          <TableCell>{ACTION_LABEL[c.action] ?? c.action}</TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AuditLogChangesModal;
